import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { selectFocusedPropertyGridCellKey } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import {
  PropertyGridFocusContext,
  PropertyGridFocusContextValue,
} from './PropertyGridFocusContext.ts';
import { PropertyGridCellKey } from './types.ts';

type PropertyGridFocusProviderProps = {
  children: React.ReactNode;
};

const focusRef = (ref: RefObject<HTMLElement>) => {
  if (!ref.current) return;
  ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  ref.current.focus();
};

export const PropertyGridFocusProvider = ({ children }: PropertyGridFocusProviderProps) => {
  const activeTargetKey = useAppSelector(selectFocusedPropertyGridCellKey);
  const registry = useRef<Map<PropertyGridCellKey, RefObject<HTMLElement>>>(new Map());
  const activeTargetKeyRef = useRef(activeTargetKey);
  activeTargetKeyRef.current = activeTargetKey;

  const registerField = useCallback(
    (cellKey: PropertyGridCellKey, cellRef: RefObject<HTMLElement>) => {
      registry.current.set(cellKey, cellRef);
      // Re-attempt focus if this newly registered field is the current target.
      // This handles the cold-navigation race condition where the provider's useEffect fires before all EditableField registrations complete.
      if (activeTargetKeyRef.current === cellKey) focusRef(cellRef);
    },
    [],
  );

  const unregisterField = useCallback((cellKey: PropertyGridCellKey) => {
    registry.current.delete(cellKey);
  }, []);

  useEffect(() => {
    if (!activeTargetKey) return;
    const ref = registry.current.get(activeTargetKey);
    if (!ref) return;
    focusRef(ref);
  }, [activeTargetKey]);

  const contextValue = useMemo<PropertyGridFocusContextValue>(
    () => ({
      activeTargetKey,
      registerField,
      unregisterField,
    }),
    [activeTargetKey, registerField, unregisterField],
  );

  return (
    <PropertyGridFocusContext.Provider value={contextValue}>
      {children}
    </PropertyGridFocusContext.Provider>
  );
};
